import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import {
  Bold, Italic, Underline as U, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Table as TableIcon, Rows3, Columns3, Trash2, Undo2, Redo2, Minus, Quote,
} from "lucide-react";

const Btn = ({ on, active, title, icon: Icon, testid }) => (
  <button
    type="button" title={title} aria-label={title} data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); on(); }}
    className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${active ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50 dark:text-slate-300 dark:hover:bg-white/10"}`}
  >
    <Icon className="h-4 w-4" />
  </button>
);

const Sep = () => <span className="w-px h-5 bg-slate-200 dark:bg-white/10 mx-1" />;

const Toolbar = ({ editor }) => {
  const c = () => editor.chain().focus();
  const inTable = editor.isActive("table");
  return (
    <div data-testid="rte-toolbar" className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-slate-200 dark:border-white/10 bg-slate-50/80 dark:bg-slate-900/60 sticky top-0 z-10">
      <Btn title="Heading 1" icon={Heading1} testid="rte-h1" active={editor.isActive("heading", { level: 1 })} on={() => c().toggleHeading({ level: 1 }).run()} />
      <Btn title="Heading 2" icon={Heading2} testid="rte-h2" active={editor.isActive("heading", { level: 2 })} on={() => c().toggleHeading({ level: 2 }).run()} />
      <Btn title="Heading 3" icon={Heading3} testid="rte-h3" active={editor.isActive("heading", { level: 3 })} on={() => c().toggleHeading({ level: 3 }).run()} />
      <Sep />
      <Btn title="Bold" icon={Bold} testid="rte-bold" active={editor.isActive("bold")} on={() => c().toggleBold().run()} />
      <Btn title="Italic" icon={Italic} testid="rte-italic" active={editor.isActive("italic")} on={() => c().toggleItalic().run()} />
      <Btn title="Underline" icon={U} testid="rte-underline" active={editor.isActive("underline")} on={() => c().toggleUnderline().run()} />
      <Btn title="Strikethrough" icon={Strikethrough} testid="rte-strike" active={editor.isActive("strike")} on={() => c().toggleStrike().run()} />
      <Sep />
      <Btn title="Bullet list" icon={List} testid="rte-bullets" active={editor.isActive("bulletList")} on={() => c().toggleBulletList().run()} />
      <Btn title="Numbered list" icon={ListOrdered} testid="rte-numbers" active={editor.isActive("orderedList")} on={() => c().toggleOrderedList().run()} />
      <Btn title="Quote" icon={Quote} testid="rte-quote" active={editor.isActive("blockquote")} on={() => c().toggleBlockquote().run()} />
      <Btn title="Divider" icon={Minus} testid="rte-hr" on={() => c().setHorizontalRule().run()} />
      <Sep />
      <Btn title="Align left" icon={AlignLeft} testid="rte-left" active={editor.isActive({ textAlign: "left" })} on={() => c().setTextAlign("left").run()} />
      <Btn title="Align centre" icon={AlignCenter} testid="rte-center" active={editor.isActive({ textAlign: "center" })} on={() => c().setTextAlign("center").run()} />
      <Btn title="Align right" icon={AlignRight} testid="rte-right" active={editor.isActive({ textAlign: "right" })} on={() => c().setTextAlign("right").run()} />
      <Sep />
      <Btn title="Insert table" icon={TableIcon} testid="rte-table" on={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} />
      {inTable && <>
        <Btn title="Add row" icon={Rows3} testid="rte-add-row" on={() => c().addRowAfter().run()} />
        <Btn title="Add column" icon={Columns3} testid="rte-add-col" on={() => c().addColumnAfter().run()} />
        <Btn title="Delete table" icon={Trash2} testid="rte-del-table" on={() => c().deleteTable().run()} />
      </>}
      <Sep />
      <Btn title="Undo" icon={Undo2} testid="rte-undo" on={() => c().undo().run()} />
      <Btn title="Redo" icon={Redo2} testid="rte-redo" on={() => c().redo().run()} />
    </div>
  );
};

export const RichTextEditor = ({ value, onChange, docKey }) => {
  const editor = useEditor({
    extensions: [
      StarterKit, Underline, TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: false }), TableRow, TableHeader, TableCell,
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: "rte-content focus:outline-none min-h-[420px] px-6 py-5", "data-testid": "rte-content" } },
  });

  useEffect(() => {
    if (editor) { editor.commands.setContent(value || "", false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, docKey]);

  if (!editor) return null;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-950 overflow-hidden min-w-0">
      <Toolbar editor={editor} />
      <div className="overflow-x-auto"><EditorContent editor={editor} /></div>
    </div>
  );
};
